import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  UnifiedTheme,
} from '@backstage/theme';

declare module '@backstage/theme' {
  interface OverrideComponentNameToClassKeys {
    PluginCatalogGraphCustomNode: 'node' | 'text' | 'clickable';
  }
}

const FONT_FAMILY =
  'Archivo, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

type Mode = 'light' | 'dark';

// Literal colours per mode: MUI derives shades from them. Overrides use CSS vars.
const PALETTES = {
  light: {
    ...palettes.light,
    mode: 'light',
    type: 'light',
    primary: {
      main: '#365EBD',
      dark: '#30539D',
      light: '#4272D7',
      contrastText: '#FFFFFF',
    },
    secondary: { main: '#4272D7', contrastText: '#FFFFFF' },
    error: { main: '#DF564D', dark: '#A33F38', light: '#FCEBEA' },
    warning: { main: '#ECA836', dark: '#7C4F0A', light: '#FDF4E0' },
    success: { main: '#61C453', dark: '#21683A', light: '#EAF7E7' },
    info: { main: '#4272D7', dark: '#365EBD', light: '#F1F5FD' },
    background: { default: '#F2F2F3', paper: '#FFFFFF' },
    text: { primary: '#04000E', secondary: '#4F4C56', disabled: '#706F76' },
    divider: '#E6E5E7',
    border: '#E6E5E7',
    errorBackground: '#FCEBEA',
    warningBackground: '#FDF4E0',
    infoBackground: '#F1F5FD',
    errorText: '#A33F38',
    warningText: '#7C4F0A',
    infoText: '#365EBD',
    link: '#4272D7',
    linkHover: '#365EBD',
    // The active fill comes from the BackstageSidebarItem `selected` override.
    navigation: {
      background: '#FFFFFF',
      indicator: 'transparent',
      color: '#04000E',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: 'var(--primary-hover-tint)' },
      submenu: { background: '#FFFFFF' },
    },
    tabbar: { indicator: '#4272D7' },
    banner: {
      info: '#4272D7',
      error: '#DF564D',
      warning: '#ECA836',
      text: '#04000E',
      link: '#4272D7',
      closeButtonColor: '#04000E',
    },
  },
  dark: {
    ...palettes.dark,
    mode: 'dark',
    type: 'dark',
    primary: {
      main: '#4272D7',
      dark: '#365EBD',
      light: '#7A9DEB',
      contrastText: '#FFFFFF',
    },
    secondary: { main: '#7A9DEB', contrastText: '#121118' },
    error: { main: '#E87C75', dark: '#B2342D', light: '#F09690' },
    warning: { main: '#ECA836', dark: '#F0BE6E', light: '#F0BE6E' },
    success: { main: '#61C453', dark: '#7ECE78', light: '#7ECE78' },
    info: { main: '#7A9DEB', dark: '#92B0F0', light: '#92B0F0' },
    background: { default: '#121118', paper: '#1B1A23' },
    text: { primary: '#F1F0F5', secondary: '#B0AEBA', disabled: '#85838F' },
    divider: '#2D2C38',
    border: '#2D2C38',
    errorBackground: '#DF564D2E',
    warningBackground: '#ECA8362E',
    infoBackground: '#4272D72E',
    errorText: '#F09690',
    warningText: '#F0BE6E',
    infoText: '#92B0F0',
    link: '#7A9DEB',
    linkHover: '#92B0F0',
    navigation: {
      background: '#1B1A23',
      indicator: 'transparent',
      color: '#F1F0F5',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: 'var(--primary-hover-tint)' },
      submenu: { background: '#1B1A23' },
    },
    tabbar: { indicator: '#7A9DEB' },
    banner: {
      info: '#4272D7',
      error: '#B2342D',
      warning: '#ECA836',
      text: '#F1F0F5',
      link: '#92B0F0',
      closeButtonColor: '#F1F0F5',
    },
  },
} as const;

// `none` drops the shape layer, whose translucent wave only vanishes on white.
const flatPage = (mode: Mode) =>
  genPageTheme({
    colors: [PALETTES[mode].background.paper, PALETTES[mode].background.paper],
    shape: 'none',
    options: { fontColor: PALETTES[mode].text.primary },
  });

const createPrimaryTheme = (mode: Mode): UnifiedTheme => {
  const page = flatPage(mode);
  return createUnifiedTheme({
    fontFamily: FONT_FAMILY,
    palette: PALETTES[mode],
    defaultPageTheme: 'home',
    pageTheme: {
      home: page,
      documentation: page,
      tool: page,
      service: page,
      website: page,
      library: page,
      other: page,
      app: page,
      apis: page,
    },
    // WARNING: MUI v4's deepmerge returns {} for a source rule that is a
    // function of props, silently deleting source and override both. Only
    // theme plain-object rules; check the shape in @backstage/core-components.
    components: {
      BackstageSidebarItem: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--primary-radius-lg)',
            margin: '2px 12px 0',
            width: 'calc(100% - 24px)',
            maxWidth: 'calc(100% - 24px)',
            height: 34,
            boxSizing: 'border-box',
            '& $itemIcon': { fontSize: 16 },
            fontWeight: 600,
          },
          // The source buttonItem rule sits after root and resets the gutter.
          buttonItem: {
            margin: '2px 12px 0',
            width: 'calc(100% - 24px)',
            maxWidth: 'calc(100% - 24px)',
          },
          selected: {
            backgroundColor: 'var(--bui-accent-bg)',
            borderLeft: 'none',
            boxShadow: 'none',
            '& svg': { color: 'var(--bui-accent-fg)' },
            // Selected is also .highlightable, whose hover would grey the pill.
            '&:hover': {
              backgroundColor: 'var(--bui-accent-bg)',
            },
          },
          // The source caps the label at 110px, ellipsizing "Performance Testing".
          label: {
            width: 'auto',
            minWidth: 0,
            fontSize: 'var(--primary-text-control)',
            fontWeight: 600,
          },
          iconContainer: {
            width: 38,
            marginRight: 0,
            justifyContent: 'flex-start',
            paddingLeft: 12,
          },
          // Always rendered; its empty 56px column starves the label.
          secondaryAction: {
            '&:empty': {
              width: 12,
              marginRight: 0,
            },
          },
        },
      },
      CatalogReactUserListPicker: {
        styleOverrides: {
          // An inline span, so the source's top margin never applied.
          title: { display: 'block', margin: '12px 12px 0' },
          groupWrapper: { margin: '8px 12px 12px' },
        },
      },
      BackstageSidebar: {
        styleOverrides: {
          drawer: {
            borderRight: '1px solid var(--bui-border-1)',
            paddingBottom: 16,
            '& hr': { background: 'var(--bui-border-1)' },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--primary-radius-2xl)',
            border: '1px solid var(--bui-border-1)',
            boxShadow: 'none',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 18,
            // v4 gives the last child a larger paddingBottom.
            '&:last-child': { paddingBottom: 18 },
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: '13px 18px',
          },
        },
      },
      // InfoCard re-declares header padding, beating MuiCardHeader.
      BackstageInfoCard: {
        styleOverrides: {
          header: {
            padding: '13px 18px',
          },
        },
      },
      // Source fills fail contrast on dark; Firefox ignores alignment-baseline on <text>.
      PluginCatalogGraphCustomNode: {
        styleOverrides: {
          node: {
            fill: 'var(--bui-bg-neutral-2)',
            stroke: 'var(--bui-border-2)',
            '&.primary': {
              fill: 'var(--bui-accent-bg)',
              stroke: 'var(--bui-accent-bg)',
            },
            '&.secondary': {
              fill: 'var(--bui-announcement-bg-subdued)',
              stroke: 'var(--bui-announcement-border)',
            },
          },
          text: {
            fill: 'var(--bui-fg-primary)',
            alignmentBaseline: 'baseline',
            dominantBaseline: 'central',
            '&.primary': { fill: 'var(--bui-accent-fg)' },
            '&.secondary': { fill: 'var(--primary-accent-ink)' },
          },
        },
      },
    },
  });
};

export const primaryLightTheme = createPrimaryTheme('light');
export const primaryDarkTheme = createPrimaryTheme('dark');
